<script setup lang="ts">
import { map } from "lodash-es";
import { Settings } from "@iconoir/vue";
import moment from "moment-timezone";
import { useFormat } from "~/composables/use-format";
import { AdminPermission } from "~~/schemas/admin-role";

defineOptions({ name: "CostReport" });

const { t } = useI18n();
const { c } = useFormat();
const { $api } = useNuxtApp();
const { can } = useAccess();
const toast = useToast();

interface CostValue {
  label: string;
  value: number;
}
interface CostSeriesRecord {
  id: string;
  label: string;
  start?: number;
  revenue: number;
  costPaid: number;
  costPlanned: number;
  values?: CostValue[];
}
interface CostAnalytics {
  overview: {
    currency: string;
    totalCost: number;
    paidCost: number;
    plannedCost: number;
  };
  series: CostSeriesRecord[];
}

const timezone = "Asia/Ho_Chi_Minh";
// Matches the picker's "Last 90 days" preset exactly, so the field shows
// that label and leaves its row active.
const filter = reactive({
  from: moment.tz(timezone).subtract(89, "days").startOf("day").unix(),
  to: moment.tz(timezone).endOf("day").unix(),
  groupBy: "month",
});
// Calendar-aligned, unlike the picker's own rolling day windows: comparing two
// periods only means anything when both are whole months. Twelve is the
// ceiling — the revenue and usage pipelines walk a day at a time and reject a
// range past 367 days.
const presets = computed(() => {
  const monthsBack = (count: number): [number, number] => [
    moment.tz(timezone).subtract(count - 1, "months").startOf("month").unix(),
    moment.tz(timezone).endOf("day").unix(),
  ];
  return [
    { label: t("dateRange.last3Months"), getValue: () => monthsBack(3) },
    { label: t("dateRange.last6Months"), getValue: () => monthsBack(6) },
    { label: t("dateRange.last12Months"), getValue: () => monthsBack(12) },
  ];
});

const query = computed(() => ({
  ...(filter.from ? { from: filter.from } : {}),
  ...(filter.to ? { to: filter.to } : {}),
  groupBy: filter.groupBy,
}));
const groupByItems = computed(() => [
  { value: "month", label: t("analytics.groupBy.month") },
  { value: "year", label: t("analytics.groupBy.year") },
]);

// The two controls are otherwise independent, which lets a range and a
// granularity combine into a chart of one bar — or a request the backend
// refuses outright. This only intervenes when the current choice would produce
// something unreadable; between two and sixty buckets it leaves it alone.
const bucketCount = (groupBy: string, from: number, to: number) => {
  const unit = groupBy === "year" ? "years" : groupBy === "month" ? "months" : "weeks";
  return moment.unix(to).diff(moment.unix(from), unit) + 1;
};

watch(
  () => [filter.from, filter.to],
  () => {
    const current = bucketCount(filter.groupBy, filter.from, filter.to);
    if (current >= 2 && current <= 60) return;
    // Coarsest first: a year of data reads as twelve months, not fifty-one
    // weeks. Three buckets is the floor for a shape to be a trend at all.
    const options = groupByItems.value.map((item) => item.value).reverse();
    const counts = options.map((value) => bucketCount(value, filter.from, filter.to));
    const fit =
      options.find((_, i) => counts[i]! >= 3 && counts[i]! <= 60) ??
      options.findLast((_, i) => counts[i]! <= 60);
    if (fit && fit !== filter.groupBy) filter.groupBy = fit;
  },
);

const firstLoading = ref(true);
// firstLoading only ever falls once; this one tracks every refetch too, so a
// filter change shows the charts working instead of sitting on stale numbers.
const loading = ref(false);
const data = ref<CostAnalytics | null>(null);
const byCategory = ref<CostSeriesRecord[]>([]);
const byVendor = ref<CostSeriesRecord[]>([]);

const labels = computed(() => map(data.value?.series ?? [], "label"));

// Each bucket carries its own category split, so the stack reads straight off
// the series instead of pivoting a flat list.
const categoryChartOption = computed(() => {
  const names = [...new Set((data.value?.series ?? []).flatMap((m) => map(m.values ?? [], "label")))];
  return {
    tooltip: { trigger: "axis" },
    legend: { data: names.map((name) => t(`finance.cost.category_${name}`)) },
    xAxis: { type: "category", data: labels.value },
    yAxis: { type: "value" },
    series: names.map((name) => ({
      name: t(`finance.cost.category_${name}`),
      type: "bar",
      stack: "cost",
      data: (data.value?.series ?? []).map((bucket) => {
        const value = (bucket.values ?? []).find((v) => v.label === name);
        return value ? Math.round(value.value * 100) / 100 : 0;
      }),
    })),
  };
});

const revenueCostChartOption = computed(() => ({
  tooltip: { trigger: "axis" },
  legend: { data: [t("analytics.chart.revenue"), t("finance.chart.costPaid"), t("finance.chart.costPlanned")] },
  xAxis: { type: "category", data: labels.value },
  yAxis: { type: "value" },
  series: [
    { name: t("analytics.chart.revenue"), type: "bar", data: map(data.value?.series ?? [], "revenue") },
    { name: t("finance.chart.costPaid"), type: "bar", data: map(data.value?.series ?? [], "costPaid") },
    { name: t("finance.chart.costPlanned"), type: "bar", data: map(data.value?.series ?? [], "costPlanned") },
  ],
}));

async function load() {
  loading.value = true;
  try {
    const [trend, category, vendor] = await Promise.all([
      $api<CostAnalytics>("/analytics/costs", { query: query.value }),
      $api<CostAnalytics>("/analytics/costs", { query: { ...query.value, groupBy: "category" } }),
      $api<CostAnalytics>("/analytics/costs", { query: { ...query.value, groupBy: "vendor" } }),
    ]);
    data.value = trend;
    byCategory.value = category.series;
    byVendor.value = vendor.series;
    firstLoading.value = false;
  } catch {
    toast.error({ title: t("message.loadFailed") });
  } finally {
    loading.value = false;
  }
}

watch(query, () => {
  load();
});

onMounted(async () => {
  await load();
});
</script>

<template>
  <div class="flex flex-col gap-large">
    <div class="flex flex-col gap-xsmall md:flex-row md:items-center md:justify-between">
      <SegmentedControl v-model="filter.groupBy" :items="groupByItems" size="small" />
      <div class="flex items-center gap-xsmall">
        <NuxtLink v-if="can(AdminPermission.CostsRead)" to="/finance/costs">
          <Button variant="secondary" size="small">
            <Settings class="icon" />
            {{ t("analytics.manageCosts") }}
          </Button>
        </NuxtLink>
        <FormDateRange
          v-model:from="filter.from"
          v-model:to="filter.to"
          :timezone="timezone"
          :presets="presets"
          size="sm"
        />
      </div>
    </div>

    <div class="grid grid-cols-2 gap-medium md:grid-cols-3">
      <MetricCard
        :loading="firstLoading"
        :label="t('analytics.kpi.totalCost')"
        :value="c(data?.overview.totalCost ?? 0, 'USD')"
      />
      <MetricCard
        :loading="firstLoading"
        :label="t('analytics.kpi.paidCost')"
        :value="c(data?.overview.paidCost ?? 0, 'USD')"
      />
      <MetricCard
        :loading="firstLoading"
        :label="t('analytics.kpi.plannedCost')"
        :value="c(data?.overview.plannedCost ?? 0, 'USD')"
      />
    </div>

    <div class="grid grid-cols-1 gap-medium lg:grid-cols-2">
      <SectionCard :title="t('analytics.chart.byCategory')">
        <Chart :option="categoryChartOption" :loading="loading" :height="300" />
      </SectionCard>
      <SectionCard :title="t('analytics.chart.revenueVsCosts')">
        <Chart :option="revenueCostChartOption" :loading="loading" :height="300" />
      </SectionCard>
    </div>

    <!-- The wrapper carries the guard as well: an empty grid is still a flex
         child, so on its own it would leave a second gap behind. -->
    <div
      v-if="firstLoading || byCategory.length || byVendor.length"
      class="grid grid-cols-1 gap-medium lg:grid-cols-2"
    >
      <Card v-if="firstLoading || byCategory.length">
        <DataTable
          :columns="[
            { key: 'label', label: t('finance.cost.category') },
            { key: 'costPaid', label: t('finance.cost.statusPaid'), align: 'right', class: 'min-w-[144px]' },
            { key: 'costPlanned', label: t('finance.cost.statusPlanned'), align: 'right', class: 'min-w-[144px]' },
            { key: 'total', label: t('finance.cost.amount'), align: 'right', class: 'min-w-[160px]' },
          ]"
          :items="byCategory"
          row-key="id"
        >
          <template #cell-label="{ row }">
            {{ t(`finance.cost.category_${row.label}`) }}
          </template>
          <template #cell-costPaid="{ row }">
            {{ c(row.costPaid, "USD") }}
          </template>
          <template #cell-costPlanned="{ row }">
            <span class="content-neutral-medium">{{ c(row.costPlanned, "USD") }}</span>
          </template>
          <template #cell-total="{ row }">
            <span class="content-neutral-strong">{{ c(row.costPaid + row.costPlanned, "USD") }}</span>
          </template>
        </DataTable>
      </Card>
      <Card v-if="firstLoading || byVendor.length">
        <DataTable
          :columns="[
            { key: 'label', label: t('finance.cost.vendor') },
            { key: 'costPaid', label: t('finance.cost.statusPaid'), align: 'right', class: 'min-w-[144px]' },
            { key: 'costPlanned', label: t('finance.cost.statusPlanned'), align: 'right', class: 'min-w-[144px]' },
            { key: 'total', label: t('finance.cost.amount'), align: 'right', class: 'min-w-[160px]' },
          ]"
          :items="byVendor"
          row-key="id"
        >
          <template #cell-costPaid="{ row }">
            {{ c(row.costPaid, "USD") }}
          </template>
          <template #cell-costPlanned="{ row }">
            <span class="content-neutral-medium">{{ c(row.costPlanned, "USD") }}</span>
          </template>
          <template #cell-total="{ row }">
            <span class="content-neutral-strong">{{ c(row.costPaid + row.costPlanned, "USD") }}</span>
          </template>
        </DataTable>
      </Card>
    </div>
  </div>
</template>
